import type {
  BillingMe,
  Deck,
  DeckSlideSpec,
  DeckSummary,
  PlanInfo,
  RunSummary,
  SessionInfo,
  SlideSpec,
} from './types'

/** Ошибка ответа с серверным кодом: по нему отличают конфликт правок от сбоя. */
export class ApiError extends Error {
  readonly code?: string

  constructor(message: string, code?: string) {
    super(message)
    this.code = code
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({ message: res.statusText }))
    throw new ApiError(body.message ?? 'Не получилось', body.code)
  }
  return res.json() as Promise<T>
}

export interface CreateResponse {
  code: string
  title: string
  adminUrl: string
  presentUrl: string
  joinUrl: string
}

export interface JoinResponse {
  participantId: string
  name: string
  code: string
  title: string
}

export const api = {
  createSession: (title: string) =>
    request<CreateResponse>('/api/sessions', { method: 'POST', body: JSON.stringify({ title }) }),

  join: (code: string, body: { name?: string; participantId?: string }) =>
    request<JoinResponse>(`/api/sessions/${code}/join`, { method: 'POST', body: JSON.stringify(body) }),

  info: (code: string) => request<SessionInfo>(`/api/sessions/${code}`),

  addSlide: (code: string, spec: SlideSpec) =>
    request<{ slideId: string }>(`/api/sessions/${code}/slides`, {
      method: 'POST',
      body: JSON.stringify(spec),
    }),

  results: (code: string) => request<Record<string, unknown>>(`/api/sessions/${code}/results`),

  decks: {
    create: (title: string) =>
      request<DeckSummary>('/api/decks', { method: 'POST', body: JSON.stringify({ title }) }),

    list: () => request<DeckSummary[]>('/api/decks'),

    get: (id: string) => request<Deck>(`/api/decks/${id}`),

    save: (id: string, body: { title: string; slides: DeckSlideSpec[]; revision: number }) =>
      request<DeckSummary>(`/api/decks/${id}`, { method: 'PUT', body: JSON.stringify(body) }),

    remove: (id: string) => request<{ ok: string }>(`/api/decks/${id}`, { method: 'DELETE' }),

    duplicate: (id: string) =>
      request<DeckSummary>(`/api/decks/${id}/duplicate`, { method: 'POST' }),

    /** Запустить демонстрацию или вернуться к идущему запуску; ответ несёт host-cookie. */
    run: (id: string) =>
      request<{ code: string; title: string; created: boolean }>(`/api/decks/${id}/runs`, {
        method: 'POST',
      }),

    runs: (id: string) => request<RunSummary[]>(`/api/decks/${id}/runs`),
  },

  runs: {
    /** Итоги конкретного запуска — снапшот целиком, только владельцу демонстрации. */
    results: (code: string) => request<Record<string, unknown>>(`/api/runs/${code}/results`),
  },

  billing: {
    /** Витрина тарифов — публичная. */
    plans: () => request<PlanInfo[]>('/api/billing/plans'),

    me: () => request<BillingMe>('/api/billing/me'),

    /** Создаёт платёж и возвращает ссылку ЮKassa; переходить нужно целиком страницей. */
    checkout: (planCode: string) =>
      request<{ confirmationUrl: string }>('/api/billing/checkout', {
        method: 'POST',
        body: JSON.stringify({ planCode }),
      }),
  },
}

/** Личность участника живёт в localStorage: это не секрет, а лишь «я тот же самый». */
export const identity = {
  pid(code: string): string | null {
    try {
      return localStorage.getItem(`clapme_pid_${code}`)
    } catch {
      return null
    }
  },
  remember(code: string, pid: string, name: string) {
    try {
      localStorage.setItem(`clapme_pid_${code}`, pid)
      localStorage.setItem('clapme_name', name)
    } catch {
      /* приватный режим — переживём, просто станем новым участником */
    }
  },
  lastName(): string {
    try {
      return localStorage.getItem('clapme_name') ?? ''
    } catch {
      return ''
    }
  },
}
